package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.EntityFinancials
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.DirectorFinancialsRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class DirectorsController(
    private val repository: DirectorFinancialsRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var directors by mutableStateOf<List<EntityFinancials>>(emptyList())
        private set

    val months: List<MonthPeriod>
        get() =
            directors
                .firstOrNull()
                ?.monthly
                ?.map { it.period }
                .orEmpty()

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyFilter(
        unit: CompanyRef?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        directors = repository.load(selection, unitId, null, directorFilter)
    }
}

@Composable
fun rememberDirectorsController(): DirectorsController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        DirectorsController(
            repository = appGraph.directorFinancialsRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
